import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import { RecommendationsPriceResponse } from '@interfaces/models/sales/domain/SellerPrice';
import { ImagesService } from '@services/image-service';
import ConditionalWrapper from '@components/common/conditional-wrapper/conditional-wrapper';
import { productImage } from '@components/orders-sales/helper';
import Link from '@components/atoms/link/link';
import styles from './product-recap.module.scss';

export interface ProductRecapC2CProps {
  selectedProduct: RecommendationsPriceResponse | null;
  productPath: string; // to remove after the field is added to RecommendationsPriceResponse endpoint
  dialogType: string;
  overrideStyles?: string;
}

const ProductRecapC2C: React.FC<ProductRecapC2CProps> = ({
  selectedProduct,
  productPath,
  dialogType,
  overrideStyles,
}) => {
  const { t: t1 } = useTranslation('catalog-management');
  const { t: t2 } = useTranslation('price-drop');

  const FALLBACK_IMAGE = ImagesService.buildUrl(`/notFound/placeholder.jpg`, { w: 40, h: 40 }, false);
  const [imageError, setImageError] = useState<boolean>(false);
  const imageOnErrorHandler = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.onerror = null;
    setImageError(true);
    if (!imageError) {
      event.currentTarget.src = FALLBACK_IMAGE;
    }
  };

  return (
    <div className={clsx(styles.productRecap, overrideStyles)}>
      <div className={styles.productRecap__info}>
        <Link href={productPath}>
          <img
            src={productImage(selectedProduct?.pictures?.[0].path)}
            alt={selectedProduct?.brand?.name}
            onError={imageOnErrorHandler}
          />
        </Link>

        <ConditionalWrapper
          condition={dialogType === 'drop-price'}
          wrap={(children) => <Link href={productPath}>{children}</Link>}
        >
          <div className={styles.productRecap__info__details}>
            <p>
              <b className={styles.productRecap__info__details__brand}>{selectedProduct?.brand.name.toUpperCase()}</b>
            </p>
            <p className="productRecap__info__details__title">{selectedProduct?.name}</p>
            {dialogType !== 'drop-price' && (
              <p>
                {t1('SELL.CATALOG_MANAGEMENT.REFERENCE')}: {selectedProduct?.id}
              </p>
            )}
            <p>
              <b
                className="productRecap__info__details__pvp"
                data-cy="product-recap-pvp"
              >
                {dialogType === 'drop-price'
                  ? `${t1('SELL.CATALOG_MANAGEMENT.CURRENT_PRICE')}: `
                  : `${t1('SELL.CATALOG_MANAGEMENT.SALE_PRICE')}: `}
                {selectedProduct.pricingBreakdown?.sellerPrice.formatted}
              </b>
            </p>
            <p>
              {dialogType === 'drop-price'
                ? `${t2('PRICE_DROP.PRICE_INFO.YOU_EARN')}: `
                : `${t1('SELL.CATALOG_MANAGEMENT.FOR_YOU')}: `}
              {selectedProduct.pricingBreakdown?.sellerEarning.formatted}
            </p>
            {dialogType !== 'drop-price' && <p>{selectedProduct.daysOnline}</p>}
          </div>
        </ConditionalWrapper>
      </div>
    </div>
  );
};

export default ProductRecapC2C;
